/** @jsxImportSource theme-ui */
import { useState } from 'react';
import { Box, Checkbox, Flex, Heading, Input, Label, Text, Textarea } from 'theme-ui';
import { toast } from 'react-toastify';
import { useRepairVossCompaniesMutation } from '@/store/apiService';
import Button from '@/components/Button';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import DataTable from '@/components/DataTable';

const companyColumns = [
    { columnKey: 'Id', accessor: 'Id', Header: 'Company Id', width: '260px' },
    { columnKey: 'Name', accessor: 'Name', Header: 'Name' },
    { columnKey: 'Reason', accessor: 'Reason', Header: 'Reason', width: '220px' },
    { columnKey: 'Details', accessor: 'Details', Header: 'Details', width: '40%' },
];

const parseGuids = (text: string): string[] =>
    text
        .split(/[\s,;]+/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0);

const Bucket = ({ title, rows }: { title: string; rows: any[] }) =>
    rows && rows.length > 0 ? (
        <Box>
            <Heading as="h4">
                {title} ({rows.length})
            </Heading>
            <DataTable data={rows} columns={companyColumns} />
        </Box>
    ) : null;

const RepairCompanies = () => {
    const [companyIdsText, setCompanyIdsText] = useState('');
    const [packageId, setPackageId] = useState('');
    const [dryRun, setDryRun] = useState(true);
    const [confirm, setConfirm] = useState(false);
    const [repair, repairState] = useRepairVossCompaniesMutation();
    const result: any = repairState.data;

    const run = () => {
        const companyIds = parseGuids(companyIdsText);
        repair({
            DryRun: dryRun,
            CompanyIds: companyIds.length > 0 ? companyIds : undefined,
            PackageId: packageId.trim() || undefined,
        })
            .unwrap()
            .then((res: any) => {
                setConfirm(false);
                toast.success(
                    dryRun
                        ? `Dry run: ${res.ToProvision} to provision, ${res.Restamped} to restamp, ${res.ManualAttention} need attention`
                        : `Enqueued ${res.Enqueued}, restamped ${res.Restamped}, ${res.ManualAttention} need attention`
                );
            })
            .catch((err) => {
                setConfirm(false);
                toast.error(err?.data?.ResponseStatus?.Message ?? err?.message ?? 'Repair failed', {
                    autoClose: 5000,
                });
            });
    };

    return (
        <Flex sx={{ flexDirection: 'column', gap: 3 }}>
            <Text>
                Finds eligible companies with no Voss customer, no subscription, a lost subscription
                stamp, or a customer shared with another company. Dry run only reports; unticking it
                enqueues provisioning, restamps licenses and unlinks shared customers.
            </Text>
            <Box>
                <Label htmlFor="repairCompanyIds">
                    Company Ids (optional, one per line or comma separated; empty = every eligible
                    company)
                </Label>
                <Textarea
                    id="repairCompanyIds"
                    rows={3}
                    value={companyIdsText}
                    onChange={(e) => setCompanyIdsText(e.target.value)}
                />
            </Box>
            <Box>
                <Label htmlFor="repairPackageId">
                    Package Id (optional, defaults to the configured package)
                </Label>
                <Input
                    id="repairPackageId"
                    value={packageId}
                    onChange={(e) => setPackageId(e.target.value)}
                />
            </Box>
            <Label sx={{ width: 'auto', alignItems: 'center' }}>
                <Checkbox checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />
                <Text>Dry run (report only)</Text>
            </Label>
            <Flex sx={{ gap: 2 }}>
                <Button
                    variant={dryRun ? 'primary' : 'danger'}
                    isLoading={repairState.isLoading}
                    onClick={() => (dryRun ? run() : setConfirm(true))}
                >
                    {dryRun ? 'Run dry run' : 'Repair now'}
                </Button>
            </Flex>
            {confirm ? (
                <ConfirmationModal
                    hideOnClickOutside
                    loading={repairState.isLoading}
                    headingText="Repair Voss companies"
                    content="This enqueues provisioning for every company that needs it, restamps lost subscription ids and unlinks customers shared with another company. Run a dry run first. Continue?"
                    onNo={() => setConfirm(false)}
                    onYes={run}
                />
            ) : null}
            {result ? (
                <Flex sx={{ flexDirection: 'column', gap: 3 }}>
                    <Text>
                        <b>{result.DryRun ? 'Dry run' : 'Repair'}</b> — total {result.Total},
                        healthy {result.Healthy}, to provision {result.ToProvision}, enqueued{' '}
                        {result.Enqueued}, restamped {result.Restamped}, manual attention{' '}
                        {result.ManualAttention}, failed {result.Failed}
                    </Text>
                    <Bucket title="To provision" rows={result.ToProvisionCompanies} />
                    <Bucket title="Restamped" rows={result.RestampedCompanies} />
                    <Bucket title="Manual attention" rows={result.ManualAttentionCompanies} />
                    <Bucket title="Failed" rows={result.FailedCompanies} />
                </Flex>
            ) : null}
        </Flex>
    );
};

export default RepairCompanies;
